/**
 * Captures the text selected in an epub.js spine iframe when the user
 * long-presses inside it, converts the selection to a CFI string, and
 * exposes everything the TextSelectionMenuWithFlashcard component needs.
 *
 * Consumers:
 *   const { selectedText, selection, cfi, menuPosition, clear, isOpen, checkNow } =
 *     useIframeSelection(rendition, { onUnsupportedSelection });
 *
 * How it works:
 *   1. Subscribes (window-level) to the `epubjs:iframe-longpress`
 *      CustomEvent dispatched by registerIframeTouchGestures.ts.
 *   2. On each event, walks rendition.getContents() (one entry per mounted
 *      spine iframe) to find the iframe whose document contains the press
 *      target.
 *   3. Reads that iframe's selection via contents.window.getSelection();
 *      if empty, bails silently. If cross-spine (start/end ownerDocument
 *      mismatch), bails AND invokes the onUnsupportedSelection callback
 *      with reason="cross-spine" so the caller can surface a toast.
 *   4. Calls contents.cfiFromRange(range) to get the CFI string.
 *   5. Computes menu coordinates in TOP-WINDOW space by offsetting the
 *      long-press iframe-local coords by the iframe element's bounding
 *      rect origin.
 *
 *   checkNow() — callable externally (e.g. from TapZoneOverlay's onLongPress
 *   callback) to scan all iframes for a non-empty selection. On mobile, the
 *   overlay sits above the iframe so iframe touch events never fire; iOS still
 *   creates selections at the system level, but neither the 500ms timer nor
 *   the mouseup path triggers. checkNow polls the iframes after the overlay
 *   detects a long-press, giving iOS time to create the selection.
 */
import { useEffect, useState, useCallback, useRef } from 'react';

interface IframeLongPressDetail {
  clientX: number;
  clientY: number;
  target: EventTarget | null;
}

export interface IframeSelectionState {
  selectedText: string;
  selection: Selection | null;
  cfi: string;
  menuPosition: { x: number; y: number };
}

export type UnsupportedSelectionReason = 'cross-spine' | 'cfi-parse-failed';

export interface UseIframeSelectionOptions {
  /** Invoked when a long-press fires but the selection can't be turned into
   *  a highlight (cross-spine range, CFI parse failure). Callers typically
   *  surface a toast. */
  onUnsupportedSelection?: (reason: UnsupportedSelectionReason) => void;
}

interface EpubJsContents {
  document: Document;
  window: Window;
  cfiFromRange(range: Range): string;
}

interface RenditionLike {
  getContents(): EpubJsContents[];
  manager?: { container?: HTMLElement };
}

export function useIframeSelection(
  rendition: RenditionLike | null,
  options: UseIframeSelectionOptions = {}
) {
  const [state, setState] = useState<IframeSelectionState | null>(null);
  // Guard against re-entrancy while the menu is open.
  const isOpenRef = useRef(false);

  // Hold the latest callback in a ref so we don't re-register the window
  // listener on every render just because the parent passed a new inline
  // function reference.
  const onUnsupportedRef = useRef(options.onUnsupportedSelection);
  useEffect(() => {
    onUnsupportedRef.current = options.onUnsupportedSelection;
  });

  const renditionRef = useRef(rendition);
  useEffect(() => {
    renditionRef.current = rendition;
  }, [rendition]);

  const clear = useCallback(() => {
    isOpenRef.current = false;
    setState(null);
  }, []);

  const processSelection = useCallback(
    (contents: EpubJsContents, sel: Selection) => {
      const range = sel.getRangeAt(0);
      const selectedText = range.toString().trim();
      if (selectedText.length === 0) return;

      if (
        range.startContainer.ownerDocument !== contents.document ||
        range.endContainer.ownerDocument !== contents.document
      ) {
        onUnsupportedRef.current?.('cross-spine');
        return;
      }

      let cfi: string;
      try {
        cfi = contents.cfiFromRange(range);
      } catch {
        onUnsupportedRef.current?.('cfi-parse-failed');
        return;
      }

      const iframeEl = contents.document.defaultView?.frameElement as
        | HTMLElement
        | null;
      const iframeRect = iframeEl?.getBoundingClientRect();
      const rangeRect = range.getBoundingClientRect();

      const menuPosition = {
        x: (iframeRect?.left ?? 0) + rangeRect.left + rangeRect.width / 2,
        y: (iframeRect?.top ?? 0) + rangeRect.top,
      };

      isOpenRef.current = true;
      setState({ selectedText, selection: sel, cfi, menuPosition });
    },
    []
  );

  const checkNow = useCallback(() => {
    const r = renditionRef.current;
    if (!r || isOpenRef.current) return;
    const allContents = r.getContents() ?? [];
    for (const contents of allContents) {
      const sel = contents.window.getSelection?.();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) continue;
      const text = sel.toString().trim();
      if (!text) continue;
      processSelection(contents, sel);
      return;
    }
  }, [processSelection]);

  useEffect(() => {
    if (!rendition) return;

    function onLongPress(ev: Event) {
      const detail = (ev as CustomEvent<IframeLongPressDetail>).detail;
      if (!detail || !rendition) return;

      const allContents = rendition.getContents() ?? [];
      const target = detail.target as Node | null;
      const contents = allContents.find(
        (c) => target && c.document.contains(target)
      );
      if (!contents) return;

      const sel = contents.window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

      processSelection(contents, sel);
    }

    window.addEventListener('epubjs:iframe-longpress', onLongPress);
    return () => window.removeEventListener('epubjs:iframe-longpress', onLongPress);
  }, [rendition, processSelection]);

  return { ...state, clear, isOpen: state !== null, checkNow };
}
